import type React from "react";
import { Button } from "@shared/ui/components/button";
import { Input } from "@shared/ui/components/input";
import { AnimatePresence, motion } from "framer-motion";
import { LinkIcon, Upload } from "lucide-react";

interface UploadMethodSelectorProps {
  uploadMethod: "file" | "url" | null;
  setUploadMethod: (method: "file" | "url" | null) => void;
  videoUrl: string | null;
  setVideoUrl: (url: string | null) => void;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onUrlContinue: () => void;
  fileInputRef?: React.RefObject<HTMLInputElement>;
  maxFileSizeText?: string;
}

/** Компактный переключатель «Файл / Ссылка» + поле URL — по образцу прототипа v4.
 *  Логика и валидация сохранены 1-в-1: меняется только вид/разметка. */
export const UploadMethodSelector: React.FC<UploadMethodSelectorProps> = ({
  uploadMethod,
  setUploadMethod,
  videoUrl,
  setVideoUrl,
  onFileSelect,
  onUrlContinue,
  fileInputRef,
  maxFileSizeText = "2 ГБ"
}) => {
  const handleFileCardClick = (): void => {
    if (uploadMethod !== "file") {
      setUploadMethod("file");
    }

    setTimeout(() => {
      fileInputRef?.current?.click();
    }, 100);
  };

  const methodCardClass = (active: boolean): string =>
    `cursor-pointer rounded-md border-2 p-4 text-center transition-all ${
      active ? "border-foreground bg-slate-50" : "border-border hover:border-muted-foreground"
    }`;

  return (
    <div className="w-full">
      {/* Переключатель способа загрузки */}
      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className={methodCardClass(uploadMethod === "file")} onClick={handleFileCardClick}>
          <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-md bg-slate-100">
            <Upload className="h-5 w-5" />
          </div>
          <h4 className="font-semibold">Загрузить файл</h4>
          <p className="mt-1 text-sm text-muted-foreground">Перетащите или выберите с компьютера</p>
          <input
            id="file-upload"
            type="file"
            accept="video/*,audio/*"
            className="hidden"
            onChange={onFileSelect}
            onClick={e => e.stopPropagation()}
            ref={fileInputRef}
          />
        </div>

        <div className={methodCardClass(uploadMethod === "url")} onClick={() => setUploadMethod("url")}>
          <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-md bg-slate-100">
            <LinkIcon className="h-5 w-5" />
          </div>
          <h4 className="font-semibold">По ссылке</h4>
          <p className="mt-1 text-sm text-muted-foreground">Вставьте URL: YouTube, VK, Rutube, OK, Дзен</p>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {uploadMethod === "url" && (
          <motion.div
            key="url-input"
            layout
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div className="flex flex-col gap-3 pt-1">
              <Input
                type="url"
                placeholder="https://www.youtube.com/watch?v=…"
                value={videoUrl || ""}
                onChange={e => setVideoUrl(e.target.value.trim())}
                className="min-h-[44px] text-base"
              />
              <Button size="lg" className="min-h-[44px] text-base" onClick={onUrlContinue} disabled={!videoUrl}>
                <LinkIcon className="mr-2 h-5 w-5" />
                Расшифровать
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-6 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span>✓ MP3, WAV, FLAC, AAC, Opus</span>
        <span>✓ MP4, MOV, AVI, MKV</span>
        <span>✓ YouTube, VK, Rutube, OK</span>
        <span>✓ до {maxFileSizeText}</span>
        <span>✓ 99 языков</span>
      </div>
    </div>
  );
};
